import { debounce } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

interface TextInputProps {
  text: string;
  modifyElement: (text: string) => void;
  /**
   * Preset sample copy ("Insert a title") the element was created with
   * (PLAT-11139). When set and the field still holds exactly this value,
   * focusing clears the field so the user can type immediately — the copy
   * stays visible as ghost placeholder text. Blurring while empty restores
   * it without committing, so the canvas element never goes blank. Leave
   * unset for user-authored text: focus then never clears anything.
   */
  placeholderText?: string;
}

export const TextInput: React.FC<TextInputProps> = props => {
  const { text, modifyElement, placeholderText } = props;
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [inputText, setInputText] = useState<string>(text);

  // Keep callback in a ref so the debounced function stays stable across
  // re-renders — otherwise its cleanup cancels pending edits before they fire.
  const modifyElementRef = useRef(modifyElement);
  useEffect(() => {
    modifyElementRef.current = modifyElement;
  }, [modifyElement]);

  useEffect(() => {
    // Skip external sync while the user is editing — async renderer round-trips
    // would otherwise overwrite the in-progress edit and jump the cursor to the end.
    if (document.activeElement === inputRef.current) return;
    setInputText(text);
  }, [text]);

  // Live-update the canvas while typing (same contract as TextAreaExtendable);
  // blur cancels the pending tick and commits immediately.
  const debouncedModify = useMemo(
    () => debounce((value: string) => modifyElementRef.current(value), 500),
    [],
  );

  useEffect(() => {
    return () => debouncedModify.cancel();
  }, [debouncedModify]);

  return (
    <TextArea
      value={inputText}
      ref={inputRef}
      placeholder={placeholderText ?? 'Enter Text'}
      onFocus={() => {
        if (placeholderText && inputText === placeholderText) {
          setInputText('');
        }
      }}
      onChange={e => {
        setInputText(e.target.value);
        debouncedModify(e.target.value);
      }}
      onBlur={e => {
        debouncedModify.cancel();
        if (placeholderText && !e.target.value) {
          setInputText(placeholderText);
          return;
        }
        modifyElement(e.target.value);
      }}
    />
  );
};

const TextArea = styled.textarea`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  box-sizing: border-box;
  display: flex;
  padding: 15px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  text-align: left;
  line-height: normal;
  background-color: transparent;
  color: var(--text-secondary);
  width: 100%;
  resize: none;

  /*
   * Bordered-field-primitive hover/focus standard — see
   * docs/design-system.md "Form Fields (Bordered Primitives)".
   * Single channel: border-color escalates on hover and focus, no
   * outline halo.
   */
  outline: none;
  transition: border-color var(--transition-fast);
  &:hover {
    border-color: var(--text-muted);
  }
  &:focus-visible {
    border-color: var(--accent);
  }
`;
